import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Table,
  Tag,
  Button,
  Progress,
  message,
  Statistic,
  Card,
  Alert,
} from "antd";
import PageHeader from "../components/generic/PageHeader";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  CameraOutlined,
  WarningOutlined,
  CopyOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { request } from "../helper/axios_helper";
import "../styles/db.css";

// ─── StepDetail ───────────────────────────────────────────────
// Fetches and shows error message + screenshot file path for a
// failed step. Screenshots are saved to disk on the server —
// not web-accessible URLs — so we show the path with a copy button.
function StepDetail({ resultLogId }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Uses existing result log detail endpoint
    request("GET", `/api/auth/result/test/detail/column/fail/${resultLogId}`)
      .then((res) => setDetail(res.data?.[0] || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [resultLogId]);

  const copyPath = (path) => {
    navigator.clipboard
      .writeText(path)
      .then(() => message.success("Path copied to clipboard"))
      .catch(() => message.error("Could not copy to clipboard"));
  };

  if (loading) {
    return (
      <div style={{ padding: "8px 48px", color: "#bbb", fontSize: 12 }}>
        Loading detail…
      </div>
    );
  }

  if (!detail) {
    return (
      <div style={{ padding: "8px 48px", color: "#bbb", fontSize: 12 }}>
        No detail recorded for this step.
      </div>
    );
  }

  // actual_value     = screenshot file path on server disk
  // raw_actual_value = clean error message (set by PlaywrightErrorUtils)
  const screenshotPath = detail.actualValue;
  const errorMessage = detail.rawData || detail.rawActualValue;

  return (
    <div
      style={{
        margin: "4px 48px",
        padding: "12px 16px",
        background: "#fff2f0",
        border: "1px solid #ffccc7",
        borderRadius: 6,
      }}
    >
      {/* ── Error message ──────────────────────────────────── */}
      {errorMessage && (
        <div style={{ marginBottom: screenshotPath ? 12 : 0 }}>
          <WarningOutlined style={{ color: "#cf1322", marginRight: 6 }} />
          <span style={{ fontWeight: 600, color: "#cf1322" }}>Error: </span>
          <span style={{ fontSize: 13, color: "#333" }}>{errorMessage}</span>
        </div>
      )}

      {/* ── Screenshot path ────────────────────────────────── */}
      {screenshotPath && screenshotPath.trim() !== "" ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#fff",
            border: "1px solid #ffccc7",
            borderRadius: 4,
            padding: "6px 10px",
          }}
        >
          <CameraOutlined style={{ color: "#888", flexShrink: 0 }} />
          <code
            style={{
              fontSize: 11,
              color: "#555",
              flex: 1,
              wordBreak: "break-all",
            }}
          >
            {screenshotPath}
          </code>
          <Button
            size="small"
            type="text"
            icon={<CopyOutlined />}
            onClick={() => copyPath(screenshotPath)}
            title="Copy path to clipboard"
          >
            Copy
          </Button>
        </div>
      ) : (
        <div
          style={{
            fontSize: 12,
            color: "#bbb",
            marginTop: errorMessage ? 8 : 0,
          }}
        >
          <CameraOutlined style={{ marginRight: 4 }} />
          No screenshot saved — configure a Screenshot Folder in suite settings.
        </div>
      )}
    </div>
  );
}

// ─── Main results page ────────────────────────────────────────
function UIAutomationResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const resultSummary = location.state?.resultSummary;

  const [stepResults, setStepResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!resultSummary) {
      navigate("/jobs");
      return;
    }

    // UI results are in app_result_log, linked by result_summary_id = jobId
    // Use the existing result endpoint — same as compare results
    const resultId =
      resultSummary.resultSummaryId ||
      resultSummary.result_summary_id ||
      resultSummary.key;

    request("GET", `/api/auth/result/test/summary/${resultId}`)
      .then((res) => setStepResults(res.data || []))
      .catch(() => message.error("Failed to load step results"))
      .finally(() => setLoading(false));
  }, [resultSummary]);

  const getStatusTag = (status) => {
    switch (status) {
      case "Pass":
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Pass
          </Tag>
        );
      case "Fail":
        return (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Fail
          </Tag>
        );
      default:
        return (
          <Tag icon={<MinusCircleOutlined />} color="default">
            Skip
          </Tag>
        );
    }
  };

  // Result log uses testResults field name (from existing ResultsSummary mapper)
  const passed = stepResults.filter((s) => s.testResults === "Pass").length;
  const failed = stepResults.filter((s) => s.testResults === "Fail").length;
  const skipped = stepResults.filter((s) => s.testResults === "Skip").length;
  const total = stepResults.length;

  const overallStatus =
    resultSummary?.testResult ||
    resultSummary?.test_result ||
    resultSummary?.jobStatus ||
    "Unknown";

  const columns = [
    {
      title: "#",
      key: "seq",
      width: "5%",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, color: "#888" }}>{index + 1}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "testResults",
      key: "testResults",
      width: "8%",
      render: getStatusTag,
      filters: [
        { text: "Pass", value: "Pass" },
        { text: "Fail", value: "Fail" },
        { text: "Skip", value: "Skip" },
      ],
      onFilter: (value, record) => record.testResults === value,
    },
    {
      title: "Step",
      dataIndex: "testName",
      key: "testName",
      render: (text) => (
        <span style={{ fontSize: 12, fontWeight: 500 }}>{text || "—"}</span>
      ),
    },
    {
      title: "Type",
      dataIndex: "testType",
      key: "testType",
      width: "12%",
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: "Pass",
      dataIndex: "totalPassCount",
      key: "totalPassCount",
      width: "6%",
      align: "center",
      render: (n) => (
        <span style={{ color: "#52c41a", fontWeight: 500 }}>{n}</span>
      ),
    },
    {
      title: "Fail",
      dataIndex: "totalFailCount",
      key: "totalFailCount",
      width: "6%",
      align: "center",
      render: (n) => (
        <span
          style={{
            color: n > 0 ? "#ff4d4f" : "#bbb",
            fontWeight: n > 0 ? 600 : 400,
          }}
        >
          {n}
        </span>
      ),
    },
  ];

  if (!resultSummary) return null;

  return (
    <div className="db-container">
      {/* ─── Header ─────────────────────────────────────────── */}
      <PageHeader
        backTo={-1}
        icon={<RobotOutlined />}
        title={
          resultSummary.testSuite ||
          resultSummary.test_suite ||
          resultSummary.suiteName ||
          "UI Test Result"
        }
        subtitle={
          <>
            {resultSummary.runStart || resultSummary.run_start}
            {(resultSummary.createdUser || resultSummary.created_user) &&
              ` · ${resultSummary.createdUser || resultSummary.created_user}`}
          </>
        }
        actions={
          <Tag
            color={
              overallStatus === "Pass"
                ? "success"
                : overallStatus === "Fail"
                  ? "error"
                  : "default"
            }
            style={{ fontSize: 14, padding: "4px 12px" }}
          >
            {overallStatus}
          </Tag>
        }
      />

      {/* ─── Stats ──────────────────────────────────────────── */}
      <Row gutter={16} style={{ margin: "16px 0" }}>
        <Col span={6}>
          <Card size="small">
            <Statistic title="Total Steps" value={total} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Passed"
              value={passed}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Failed"
              value={failed}
              valueStyle={{ color: failed > 0 ? "#ff4d4f" : undefined }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Skipped"
              value={skipped}
              valueStyle={{ color: "#bbb" }}
            />
          </Card>
        </Col>
      </Row>

      {/* ─── Progress bar ───────────────────────────────────── */}
      <Row style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Progress
            percent={total > 0 ? Math.round((passed / total) * 100) : 0}
            format={() => `${passed} / ${total} passed`}
            status={
              failed > 0
                ? "exception"
                : passed === total && total > 0
                  ? "success"
                  : "normal"
            }
          />
        </Col>
      </Row>

      {/* ─── Failure alert ──────────────────────────────────── */}
      {failed > 0 && (
        <Alert
          type="error"
          showIcon
          message={`${failed} step${failed > 1 ? "s" : ""} failed`}
          description="Expand a failed row to see the error message and screenshot file path."
          style={{ marginBottom: 16 }}
          closable
        />
      )}

      {/* ─── Steps table ────────────────────────────────────── */}
      <Row className="table-container">
        <Table
          loading={loading}
          columns={columns}
          dataSource={stepResults}
          rowKey={(record) => record.key || record.resultLogId}
          expandable={{
            expandedRowRender: (record) => (
              <StepDetail resultLogId={record.key || record.resultLogId} />
            ),
            rowExpandable: (record) => record.testResults === "Fail",
          }}
          rowClassName={(record) =>
            record.testResults === "Fail" ? "table-row-fail" : ""
          }
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: ["20", "50", "100"],
          }}
          size="small"
        />
      </Row>
    </div>
  );
}

export default UIAutomationResults;
